/**
 * Data layer for `advertiser-commission`. Thin typed fetch wrappers over
 * /api/v1/advertiser-commission. Raw `fetch()` is confined to this file per
 * the dash contract (D6).
 *
 * The API shape mirrors apps/api/src/routes/advertiser-commission.ts:
 *   - POST   /                        — create a rule (admin)
 *   - GET    /advertiser/:advertiserId — list rules scoped to advertiser
 *   - PATCH  /:id                     — partial update (admin)
 *   - DELETE /:id                     — remove (admin)
 */

export type RuleType = "percent" | "fixed";

export interface AdvertiserCommissionRule {
	id: string;
	advertiser_id: string;
	rule_type: RuleType;
	rate_bps: number;
	fixed_cents: number;
	currency: string;
	priority: number;
	effective_from: number;
	effective_to: number | null;
	created_at: number;
	updated_at: number;
}

export interface ListByAdvertiserResponse {
	advertiser_id: string;
	rules: AdvertiserCommissionRule[];
}

export interface CreateRuleBody {
	advertiserId: string;
	ruleType: RuleType;
	rateBps?: number;
	fixedCents?: number;
	currency: string;
	priority?: number;
	effectiveFrom: number;
	effectiveTo?: number | null;
}

const BASE = "/api/v1/advertiser-commission";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listByAdvertiser(
	advertiserId: string,
): Promise<ListByAdvertiserResponse> {
	const res = await fetch(
		`${BASE}/advertiser/${encodeURIComponent(advertiserId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListByAdvertiserResponse>(res);
}

export async function create(
	body: CreateRuleBody,
): Promise<{ rule: AdvertiserCommissionRule }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ rule: AdvertiserCommissionRule }>(res);
}

export async function remove(id: string): Promise<{ deleted: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ deleted: boolean; id: string }>(res);
}

export const advertiserCommissionQueryKeys = {
	all: ["advertiser-commission"] as const,
	list: () => [...advertiserCommissionQueryKeys.all] as const,
	listByAdvertiser: (advertiserId: string) =>
		[...advertiserCommissionQueryKeys.all, "advertiser", advertiserId] as const,
	detail: (id: string) => [...advertiserCommissionQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Pure data-layer helpers — used by the page + unit-tested directly.
// ---------------------------------------------------------------------------

/**
 * Compute the payout (in minor currency units, e.g. cents) for a given
 * subtotal under a commission rule. Percent rules multiply by rate_bps/10000;
 * fixed rules return fixed_cents regardless of subtotal.
 *
 * Returns an integer (floored) to match on-chain / ledger accounting.
 */
export function computePayoutCents(
	rule: Pick<AdvertiserCommissionRule, "rule_type" | "rate_bps" | "fixed_cents">,
	subtotalCents: number,
): number {
	if (!Number.isFinite(subtotalCents) || subtotalCents < 0) return 0;
	if (rule.rule_type === "fixed") {
		return Math.max(0, Math.floor(rule.fixed_cents));
	}
	// percent: rate_bps out of 10000
	const bps = Math.max(0, Math.min(10_000, Math.floor(rule.rate_bps)));
	return Math.floor((subtotalCents * bps) / 10_000);
}

/**
 * Human-readable rate preview for a rule.
 *   percent  -> "2.50%"  (always two decimals for consistent alignment)
 *   fixed    -> "$1.50 USD"
 */
export function formatRatePreview(
	rule: Pick<
		AdvertiserCommissionRule,
		"rule_type" | "rate_bps" | "fixed_cents" | "currency"
	>,
): string {
	if (rule.rule_type === "fixed") {
		const whole = Math.floor(rule.fixed_cents / 100);
		const frac = Math.abs(rule.fixed_cents % 100).toString().padStart(2, "0");
		return `$${whole}.${frac} ${rule.currency}`;
	}
	const pct = rule.rate_bps / 100;
	return `${pct.toFixed(2)}%`;
}

/**
 * Filter a list of rules by advertiser id (case-insensitive substring match).
 * An empty query returns the full list.
 */
export function filterRulesByAdvertiser<
	T extends Pick<AdvertiserCommissionRule, "advertiser_id">,
>(rules: T[], query: string): T[] {
	const q = query.trim().toLowerCase();
	if (!q) return rules.slice();
	return rules.filter((r) => r.advertiser_id.toLowerCase().includes(q));
}
